<template>
    <div class="c-upload-section">
        <ion-grid>
            <ion-row :style="{ 'flex-direction': flexDirection }">
                <ion-col
                    size="3"
                    size-md="2"
                    v-for="(existingFile, index) in existingFiles"
                    :key="'existingFile' + index"
                >
                    <ion-row>
                        <ion-col class="u-p-top-none">
                            <div
                                :style="
                                    'background-image: url(' +
                                    $store.state.dokbox.base64Images[
                                        existingFile.fileHash
                                    ] +
                                    ')'
                                "
                                class="c-filepreview"
                                :class="{
                                    'c-filepreview__pdf':
                                        existingFile.fileType === 'pdf',
                                    'u-cursor-pointer':
                                        existingFile.downloadUrl,
                                }"
                                @click="
                                    downloadFile(
                                        baseurl + existingFile.downloadUrl,
                                        existingFile.downloadUrl
                                    )
                                "
                            >
                                <ion-button
                                    v-if="!disabled"
                                    shape="round"
                                    size="small"
                                    @click.stop="
                                        $emit(
                                            'delete-existing-file',
                                            existingFile.id
                                        )
                                    "
                                >
                                    <ion-icon
                                        slot="icon-only"
                                        name="close"
                                    ></ion-icon>
                                </ion-button>
                            </div>
                        </ion-col>
                    </ion-row>
                    <ion-row v-if="existingFile.downloadUrl">
                        <ion-col class="ion-text-center ion-no-padding">
                            <ion-button
                                fill="clear"
                                size="small"
                                @click="
                                    downloadFile(
                                        baseurl + existingFile.downloadUrl
                                    )
                                "
                                ><ion-icon
                                    slot="icon-only"
                                    name="download"
                                ></ion-icon
                            ></ion-button>
                        </ion-col>
                    </ion-row>
                </ion-col>

                <ion-col
                    size="3"
                    size-md="2"
                    v-for="(base64File, index) in base64Files"
                    :key="'base64File' + index"
                >
                    <div
                        :style="'background-image: url(' + base64File + ')'"
                        class="c-filepreview"
                        :class="{
                            'c-filepreview__pdf': base64File.startsWith(
                                'data:application/pdf'
                            ),
                        }"
                    >
                        <ion-button
                            shape="round"
                            size="small"
                            @click="handleFileDelete(index)"
                            v-if="!shouldGetGenuineImages"
                        >
                            <ion-icon slot="icon-only" name="close"></ion-icon>
                        </ion-button>
                    </div>
                </ion-col>

                <ion-col size="3" size-md="2">
                    <div class="c-fileinput" v-if="showAddButton && !disabled">
                        <input
                            type="file"
                            :multiple="multiple"
                            :id="id"
                            ref="files"
                            @change="handleFilesUpload"
                            accept="image/*, application/pdf"
                            @click="resetFilesUpload"
                        />
                        <label :for="id" @click="getNativePhoto">
                            <ion-icon name="add"></ion-icon>
                        </label>
                    </div>
                </ion-col>
            </ion-row>
        </ion-grid>
        <ion-button
            v-if="shouldGetGenuineImages && base64Files.length > 0"
            fill="clear"
            @click="getNativePhoto"
            color="primary"
            >Erneut scannen</ion-button
        >
        <div>
            <input-error :id="id"></input-error>
        </div>
    </div>
</template>

<script>
import { BASEURL } from "@/utils/globals";
import InputError from "./InputError";
import { handleError } from "@/utils/error";
import { Plugins, CameraResultType } from "@capacitor/core";
import { file } from "@/mixins/file";

const { Camera, GenuineIDPlugin } = Plugins;

export default {
    mixins: [file],
    components: { InputError },
    props: {
        value: {
            type: Array,
            required: true,
        },
        existingFiles: {
            type: Array,
            required: false,
            default: () => {
                return [];
            },
        },
        id: {
            type: String,
            required: true,
        },
        multiple: {
            type: Boolean,
            default: false,
            required: false,
        },
        shouldGetNativePhoto: {
            type: Boolean,
            default: false,
            required: false,
        },
        maxFiles: {
            type: Number,
            default: 10,
            required: false,
        },
        reset: {
            type: Boolean,
            required: false,
            default: false,
        },
        shouldGetGenuineImages: {
            type: Boolean,
            default: false,
            required: false,
        },
        flexDirection: {
            type: String,
            required: false,
            default: "row",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        reset(value) {
            if (value) {
                this.base64Files = [];
            }
        },
    },
    data() {
        return {
            base64Files: [],
            baseurl: BASEURL,
        };
    },
    computed: {
        showAddButton() {
            let result = true;
            if (
                this.value.length + this.existingFiles.length > 0 &&
                (!this.multiple ||
                    this.value.length + this.existingFiles.length >=
                        this.maxFiles)
            ) {
                result = false;
            }
            return result;
        },
    },
    methods: {
        async openGenuineID() {
            const result = await GenuineIDPlugin.openGenuineID();
            if (result.frontImage) {
                this.value = [];
                this.base64Files = [];

                let frontImage = result.frontImage;
                let backImage = result.backImage;
                let faceImage = result.faceImage;
                let imageType = "image/jpeg";
                if (frontImage.startsWith("data:")) {
                    imageType = frontImage.substring(
                        frontImage.indexOf(":") + 1,
                        frontImage.indexOf(";")
                    );
                    frontImage = frontImage.split(",")[1];
                    if (backImage) backImage = backImage.split(",")[1];
                    if (faceImage) faceImage = faceImage.split(",")[1];
                }

                this.addFile(this.base64toBlob(frontImage, imageType));
                if (backImage)
                    this.addFile(this.base64toBlob(backImage, imageType));
                if (faceImage)
                    this.addFile(this.base64toBlob(faceImage, imageType));
                this.$emit("genuineChange", result.completeJsonPayload);
            }
        },
        async getNativePhoto(e) {
            if (this.shouldGetGenuineImages && Capacitor.isNative) {
                e.preventDefault();
                this.openGenuineID();
                // this.$ionic.alertController
                //     .create({
                //         header: "Wichtige Informationen",
                //         message:
                //             "Bitte achte bei den kommenden Scan-Vorgängen auf <b>gute Lichtverhältnisse</b>. Unser Tipp: Am besten geeignet ist natürliches Tageslicht ohne direkte Sonneneinstrahlung.<br><br>Falls du Probleme beim Scannen hast, kannst du die Aktivierung auch über unsere Website unter <a href='https://www.klickmal.at/content/dam/klickmal/servicebereich/#/aktivierung' target='_blank'>klickmal.at/aktivierung</a> durchführen.",
                //         buttons: ["Weiter"],
                //     })
                //     .then((a) => {
                //         a.onDidDismiss().then(() => {
                //             this.openGenuineID();
                //         });
                //         a.present();
                //     });
            } else if (
                this.shouldGetNativePhoto &&
                Capacitor.isNative &&
                Capacitor.platform === "android"
            ) {
                e.preventDefault();
                const image = await Camera.getPhoto({
                    quality: 90,
                    allowEditing: false,
                    resultType: CameraResultType.Base64,
                });
                // image.webPath will contain a path that can be set as an image src.
                // You can access the original file using image.path, which can be
                // passed to the Filesystem API to read the raw data of the image,
                // if desired (or pass resultType: CameraResultType.Base64 to getPhoto)
                // var imageUrl = image.webPath;
                // var originalImage = image.path;
                this.addFile(
                    this.base64toBlob(
                        image.base64String,
                        "image/" + image.format
                    )
                );
                // Can be set to the src of an image now
                // imageElement.src = imageUrl;
            }
        },
        resetFilesUpload() {
            this.$refs.files.value = null;
        },
        handleFilesUpload() {
            let filesIndex = 0;
            for (let newFile of this.$refs.files.files) {
                if (this.value.length + filesIndex >= this.maxFiles) {
                    return false;
                }
                let fileFound = false;
                for (let file of this.value) {
                    if (
                        newFile.name === file.name &&
                        newFile.size === file.size
                    ) {
                        fileFound = true;
                        break;
                    }
                }
                if (!fileFound) {
                    loadImage.parseMetaData(newFile, (data) => {
                        if (
                            data !== undefined &&
                            data.exif &&
                            data.exif.get("Orientation")
                        ) {
                            let orientation = data.exif.get("Orientation");
                            loadImage(
                                newFile,
                                (img) => {
                                    let dataURL = img.toDataURL("image/jpeg");
                                    let base64 = dataURL.replace(
                                        /^data:image\/(png|jpeg);base64,/,
                                        ""
                                    );
                                    this.addFile(
                                        this.base64toBlob(base64, "image/jpeg")
                                    );
                                },
                                {
                                    orientation: orientation,
                                }
                            );
                        } else {
                            // use default implementation for PNG, etc.
                            this.addFile(newFile);
                        }
                    });
                }
                filesIndex++;
            }
        },
        addFile(newFile) {
            this.value.push(newFile);
            const reader = new FileReader();
            reader.readAsDataURL(newFile);
            reader.onload = () => {
                this.base64Files.push(reader.result);
            };
            reader.onerror = (error) => {
                handleError(error);
            };
            this.$emit("input", this.value);
        },
        handleFileDelete(index) {
            this.value.splice(index, 1);
            this.base64Files.splice(index, 1);
            this.$emit("input", this.value);
        },
    },
};
</script>

<style lang="scss" scoped>
.c-upload-section {
    width: 100%;
    // border: solid 2px red;
}
.c-filepreview {
    // height: 85px;
    // width: 85px;
    width: 100%;
    background-position: center;
    background-size: cover;
    display: inline-block;
    vertical-align: middle;
    margin-right: var(--gangl-padding-small);
    // margin-bottom: var(--gangl-padding-small);
    border: solid 1px var(--ion-color-medium-shade);
    border-radius: var(--gangl-border-radius);
    position: relative;

    &:after {
        content: "";
        display: block;
        padding-bottom: 100%;
    }

    &__pdf {
        background-image: url("../assets/img/pdf_thumb.jpg") !important;
    }

    ion-button {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 10px;
        border-radius: 85px;
        border: solid 1px var(--ion-color-medium-shade);

        width: 23px;
        height: 23px;
        --border-radius: 85px;
        --background: #fff;
        --padding-start: 0;
        --padding-end: 0;
        --padding-top: 0;
        --padding-bottom: 0;
        --color: black;
    }
}

.c-fileinput {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: var(--gangl-padding-small);
    width: 100%;

    ion-icon {
        height: 100%;
    }
}

input[type="file"] {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    // STOP: produces ion-checkbox bug (not updating correctly)
    // z-index: -1;
}

input[type="file"] + label {
    // height: 85px;
    // width: 85px;
    // line-height: 100%;
    // text-align: center;
    // font-size: 50px;
    border: solid 1px var(--ion-color-medium-shade);
    border-radius: var(--gangl-border-radius);
    color: var(--ion-color-medium-shade);
    background-color: #fff;
    display: inline-block;
    width: 100%;

    position: relative;

    ion-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 50%;
    }

    &:after {
        content: "";
        display: block;
        padding-bottom: 100%;
    }
}

// input[type="file"]:active + label,
// input[type="file"] + label:hover {
//   background-color: var(--ion-color-primary);
// }

input[type="file"] + label {
    cursor: pointer; /* "hand" cursor */
}
</style>
